import { useState, useMemo, useCallback } from "react";
import { obtenerToken } from '../lib/auth';

// ─── Holiday data ───────────────────────────────────────────────────
const FIESTAS_NACIONALES = {
  fixed: [
    { m: 1, d: 1, name: "Año Nuevo" },
    { m: 1, d: 6, name: "Epifanía del Señor" },
    { m: 5, d: 1, name: "Fiesta del Trabajo" },
    { m: 8, d: 15, name: "Asunción de la Virgen" },
    { m: 10, d: 12, name: "Fiesta Nacional de España" },
    { m: 11, d: 1, name: "Todos los Santos" },
    { m: 12, d: 6, name: "Día de la Constitución" },
    { m: 12, d: 8, name: "Inmaculada Concepción" },
    { m: 12, d: 25, name: "Natividad del Señor" },
  ],
};

const FIESTAS_CATALUNA = [
  { m: 6, d: 24, name: "Sant Joan" },
  { m: 9, d: 11, name: "Diada Nacional de Catalunya" },
  { m: 12, d: 26, name: "Sant Esteve" },
];

// Festes locals oficials (DOGC). 2026 confirmadas; 2027 pendiente de publicación (editable).
const FESTES_LOCALS = {
  barcelona: { nombre: "Barcelona", 2026: ["2026-05-25", "2026-09-24"] },
  girona: { nombre: "Girona", 2026: ["2026-07-25", "2026-10-29"] },
  figueres: { nombre: "Figueres", 2026: ["2026-05-25", "2026-06-29"] },
  olot: { nombre: "Olot", 2026: ["2026-09-07", "2026-09-08"] },
  ripoll: { nombre: "Ripoll", 2026: ["2026-05-11", "2026-05-12"] },
  blanes: { nombre: "Blanes", 2026: ["2026-04-07", "2026-08-21"] },
  sant_feliu: { nombre: "Sant Feliu de Guíxols", 2026: ["2026-02-13", "2026-08-01"] },
  la_bisbal: { nombre: "La Bisbal d'Empordà", 2026: ["2026-05-25", "2026-08-17"] },
  santa_coloma: { nombre: "Santa Coloma de Farners", 2026: ["2026-04-07", "2026-09-24"] },
};

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function getMovableHolidays(year) {
  const easter = easterSunday(year);
  return [
    { date: addDays(easter, -3), name: "Jueves Santo" },
    { date: addDays(easter, -2), name: "Viernes Santo" },
    { date: addDays(easter, 1), name: "Lunes de Pascua (Cat.)" },
  ];
}

function getAllHolidays(year, ccaa) {
  const holidays = new Set();
  const holidayNames = {};

  const addH = (d, name) => {
    const key = d.toISOString().slice(0, 10);
    holidays.add(key);
    holidayNames[key] = name;
  };

  FIESTAS_NACIONALES.fixed.forEach((h) => addH(new Date(year, h.m - 1, h.d), h.name));

  const movable = getMovableHolidays(year);
  movable.forEach((h) => {
    if (h.name !== "Lunes de Pascua (Cat.)" || ccaa === "cataluna") {
      addH(h.date, h.name);
    }
  });

  if (ccaa === "cataluna") {
    FIESTAS_CATALUNA.forEach((h) => addH(new Date(year, h.m - 1, h.d), h.name));
  }

  return { holidays, holidayNames };
}

// ─── Core logic ─────────────────────────────────────────────────────
function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}
function isInAugust(date) {
  return date.getMonth() === 7;
}
function isHabil(date, holidays, augustoInhabil) {
  if (isWeekend(date)) return false;
  const key = date.toISOString().slice(0, 10);
  if (holidays.has(key)) return false;
  if (augustoInhabil && isInAugust(date)) return false;
  return true;
}
function nextHabil(date, holidays, augustoInhabil) {
  let d = new Date(date);
  while (!isHabil(d, holidays, augustoInhabil)) {
    d = addDays(d, 1);
  }
  return d;
}

function formatDate(d) {
  const days = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
  const months = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  return `${days[d.getDay()]} ${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
}
function formatDateShort(d) {
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function calcularPlazo({ fechaNotificacion, cantidad, unidad, ccaa, augustoInhabil, festivosLocales = [] }) {
  const start = new Date(fechaNotificacion + "T00:00:00");
  if (isNaN(start.getTime())) return null;

  const yearsNeeded = [start.getFullYear(), start.getFullYear() + 1, start.getFullYear() + 2];
  let allHolidays = new Set();
  let allHolidayNames = {};
  yearsNeeded.forEach((y) => {
    const { holidays, holidayNames } = getAllHolidays(y, ccaa);
    holidays.forEach((h) => allHolidays.add(h));
    Object.assign(allHolidayNames, holidayNames);
  });
  // Festivos locales (editables por el usuario)
  festivosLocales.filter(Boolean).forEach((iso) => {
    allHolidays.add(iso);
    allHolidayNames[iso] = "Festa local";
  });

  const log = [];
  let diaInicio = addDays(start, 1);

  if (unidad === "dias_habiles") {
    let current = new Date(diaInicio);
    if (!isHabil(current, allHolidays, augustoInhabil)) {
      const old = new Date(current);
      current = nextHabil(current, allHolidays, augustoInhabil);
      log.push(`El primer día hábil tras la notificación (${formatDate(old)}) es inhábil → se traslada al ${formatDate(current)}.`);
    }
    let count = 1;
    while (count < cantidad) {
      current = addDays(current, 1);
      if (isHabil(current, allHolidays, augustoInhabil)) count++;
    }
    log.push(`Plazo de ${cantidad} días hábiles desde el ${formatDate(diaInicio)}.`);
    log.push(`El plazo vence el ${formatDate(current)}.`);
    return { fechaVencimiento: current, fechaNotificacion: start, log, holidayNames: allHolidayNames };
  }

  if (unidad === "dias_naturales") {
    let vencimiento = addDays(diaInicio, cantidad - 1);
    log.push(`Plazo de ${cantidad} días naturales desde el ${formatDate(diaInicio)}.`);
    if (!isHabil(vencimiento, allHolidays, augustoInhabil)) {
      const old = new Date(vencimiento);
      vencimiento = nextHabil(vencimiento, allHolidays, augustoInhabil);
      log.push(`El día ${formatDate(old)} es inhábil → se traslada al siguiente día hábil: ${formatDate(vencimiento)}.`);
    }
    log.push(`El plazo vence el ${formatDate(vencimiento)}.`);
    return { fechaVencimiento: vencimiento, fechaNotificacion: start, log, holidayNames: allHolidayNames };
  }

  if (unidad === "meses") {
    let vencimiento = new Date(diaInicio);
    vencimiento.setMonth(vencimiento.getMonth() + cantidad);
    vencimiento = addDays(vencimiento, -1);
    log.push(`Plazo de ${cantidad} mes(es) de fecha a fecha desde el ${formatDate(diaInicio)} (art. 133.3 LEC).`);
    if (!isHabil(vencimiento, allHolidays, augustoInhabil)) {
      const old = new Date(vencimiento);
      vencimiento = nextHabil(vencimiento, allHolidays, augustoInhabil);
      log.push(`El día ${formatDate(old)} es inhábil → se traslada al siguiente día hábil: ${formatDate(vencimiento)}.`);
    }
    log.push(`El plazo vence el ${formatDate(vencimiento)}.`);
    return { fechaVencimiento: vencimiento, fechaNotificacion: start, log, holidayNames: allHolidayNames };
  }

  if (unidad === "anios") {
    let vencimiento = new Date(diaInicio);
    vencimiento.setFullYear(vencimiento.getFullYear() + cantidad);
    vencimiento = addDays(vencimiento, -1);
    log.push(`Plazo de ${cantidad} año(s) de fecha a fecha desde el ${formatDate(diaInicio)}.`);
    if (!isHabil(vencimiento, allHolidays, augustoInhabil)) {
      const old = new Date(vencimiento);
      vencimiento = nextHabil(vencimiento, allHolidays, augustoInhabil);
      log.push(`El día ${formatDate(old)} es inhábil → se traslada al siguiente día hábil: ${formatDate(vencimiento)}.`);
    }
    log.push(`El plazo vence el ${formatDate(vencimiento)}.`);
    return { fechaVencimiento: vencimiento, fechaNotificacion: start, log, holidayNames: allHolidayNames };
  }

  return null;
}

// ─── Styles ─────────────────────────────────────────────────────────
const palette = {
  bg: "#F7F6F3", card: "#FFFFFF", primary: "#1B3A5C", primaryLight: "#2A5580",
  accent: "#C4A35A", accentLight: "#E8D5A0", text: "#2D2D2D", textMuted: "#6B7280",
  border: "#D6D3CC", success: "#1A6B4A", successBg: "#E8F5EE", danger: "#9B2C2C",
  dangerBg: "#FFF0F0", inputBg: "#FAFAF8",
};
const fonts = {
  display: "'Georgia', 'Times New Roman', serif",
  body: "'Segoe UI', system-ui, -apple-system, sans-serif",
  mono: "'Consolas', 'Courier New', monospace",
};
const lblStyle = { display: "block", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: palette.textMuted, marginBottom: 6 };
const inputStyle = { width: "100%", padding: "10px 12px", border: `1px solid ${palette.border}`, borderRadius: 6, fontSize: 15, fontFamily: fonts.body, background: palette.inputBg, color: palette.text, boxSizing: "border-box" };
const cardStyle = { background: palette.card, borderRadius: 10, border: `1px solid ${palette.border}`, padding: "20px", marginBottom: 14 };

// ─── Component ──────────────────────────────────────────────────────
export default function CalculadoraPlazos() {
  const today = new Date().toISOString().slice(0, 10);

  const [jurisdiccion, setJurisdiccion] = useState("civil");
  const [unidad, setUnidad] = useState("dias_habiles");
  const [cantidad, setCantidad] = useState(20);
  const [fechaNotificacion, setFechaNotificacion] = useState(today);
  const [ccaa, setCcaa] = useState("cataluna");
  const [augustoManual, setAugustoManual] = useState(null);
  const [municipio, setMunicipio] = useState("girona");
  const [festaLocal1, setFestaLocal1] = useState("");
  const [festaLocal2, setFestaLocal2] = useState("");
  const [resultado, setResultado] = useState(null);

  // Panel guardar en expediente + agenda
  const API = process.env.REACT_APP_BACKEND_URL || "";
  const authToken = () => obtenerToken();
  const [showGuardar, setShowGuardar] = useState(false);
  const [expQuery, setExpQuery] = useState("");
  const [expOpciones, setExpOpciones] = useState([]);
  const [expSel, setExpSel] = useState(null);
  const [tipoPlazoTexto, setTipoPlazoTexto] = useState("");
  const [crearEvento, setCrearEvento] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardarRes, setGuardarRes] = useState(null);
  const [guardarErr, setGuardarErr] = useState("");

  const prefillFestes = useCallback((muni, fechaRef) => {
    const year = (fechaRef || today).slice(0, 4);
    const data = FESTES_LOCALS[muni];
    const dates = data && data[year] ? data[year] : ["", ""];
    setFestaLocal1(dates[0] || "");
    setFestaLocal2(dates[1] || "");
  }, [today]);

  const augustoInhabil = useMemo(() => {
    if (augustoManual !== null) return augustoManual;
    return jurisdiccion === "civil" || jurisdiccion === "contencioso";
  }, [jurisdiccion, augustoManual]);

  const handleJurisdiccionChange = (val) => {
    setJurisdiccion(val);
    setAugustoManual(null);
    setResultado(null);
  };

  const calcular = useCallback(() => {
    const r = calcularPlazo({
      fechaNotificacion, cantidad: parseInt(cantidad, 10), unidad, ccaa, augustoInhabil,
      festivosLocales: [festaLocal1, festaLocal2],
    });
    setResultado(r);
    setGuardarRes(null);
    setGuardarErr("");
  }, [fechaNotificacion, cantidad, unidad, ccaa, augustoInhabil, festaLocal1, festaLocal2]);

  const buscarExp = useCallback(async (q) => {
    setExpQuery(q); setExpSel(null);
    if (q.length < 2) { setExpOpciones([]); return; }
    try {
      const res = await fetch(`${API}/api/expedientes?search=${encodeURIComponent(q)}&limit=8`, {
        headers: { Authorization: `Bearer ${authToken()}` },
      });
      const data = await res.json();
      setExpOpciones(data.data || data || []);
    } catch (e) { setExpOpciones([]); }
  }, [API]);

  const guardarEnExpediente = useCallback(async () => {
    setGuardarErr(""); setGuardarRes(null);
    if (!expSel) { setGuardarErr("Selecciona un expediente."); return; }
    if (!tipoPlazoTexto.trim()) { setGuardarErr("Describe el tipo de plazo."); return; }
    if (!resultado || resultado.error) { setGuardarErr("Primero calcula el plazo."); return; }
    setGuardando(true);
    try {
      const v = resultado.fechaVencimiento;
      const iso = `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, "0")}-${String(v.getDate()).padStart(2, "0")}`;
      const res = await fetch(`${API}/api/plazos/guardar-en-expediente`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({
          expedienteId: expSel.id,
          tipoPlazoTexto: tipoPlazoTexto.trim(),
          fechaVencimiento: iso,
          fechaNotificacion,
          detalle: (resultado.log || []).join("\n"),
          crearEvento,
          alertas: ["1dia", "1hora"],
        }),
      });
      const data = await res.json();
      if (!res.ok) setGuardarErr(data.detail || "Error al guardar el plazo.");
      else setGuardarRes(data);
    } catch (e) { setGuardarErr("Error de conexión."); }
    setGuardando(false);
  }, [expSel, tipoPlazoTexto, resultado, fechaNotificacion, crearEvento, API]);


  const jurisdicciones = [
    { value: "civil", label: "Civil", icon: "⚖️" },
    { value: "contencioso", label: "Contencioso-Administrativo", icon: "🏛️" },
    { value: "social", label: "Social / Laboral", icon: "👷" },
    { value: "penal", label: "Penal", icon: "🔒" },
  ];
  const unidades = [
    { value: "dias_habiles", label: "Días hábiles" },
    { value: "dias_naturales", label: "Días naturales" },
    { value: "meses", label: "Meses" },
    { value: "anios", label: "Años" },
  ];
  const ccaaOptions = [
    { value: "cataluna", label: "Catalunya" },
    { value: "nacional", label: "Resto de España (solo fiestas nacionales)" },
  ];
  const presets = [
    { label: "5 días", cantidad: 5, unidad: "dias_habiles" },
    { label: "10 días", cantidad: 10, unidad: "dias_habiles" },
    { label: "20 días", cantidad: 20, unidad: "dias_habiles" },
    { label: "1 mes", cantidad: 1, unidad: "meses" },
    { label: "2 meses", cantidad: 2, unidad: "meses" },
    { label: "30 días nat.", cantidad: 30, unidad: "dias_naturales" },
  ];

  return (
    <div style={{ background: palette.bg, borderRadius: 12, border: `1px solid ${palette.border}`, overflow: "hidden", fontFamily: fonts.body, color: palette.text }} data-testid="calculadora-plazos">
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryLight} 100%)`, padding: "24px 20px 18px", color: "white" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <span style={{ fontSize: 26 }}>📅</span>
            <h1 style={{ fontFamily: fonts.display, fontSize: 21, fontWeight: 700, margin: 0, letterSpacing: "-0.01em" }}>
              Cálculo de Plazos Procesales
            </h1>
          </div>
          <p style={{ fontSize: 12, opacity: 0.8, margin: "4px 0 0 38px", fontStyle: "italic" }}>
            Arts. 130-136 LEC · Art. 183 LOPJ · Lex Duo Advocats
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "18px 14px 28px" }}>
        {/* Jurisdicción */}
        <div style={{ ...cardStyle, padding: "20px 20px 16px" }}>
          <label style={{ ...lblStyle, marginBottom: 10 }}>Jurisdicción</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {jurisdicciones.map((j) => (
              <button
                key={j.value}
                data-testid={`plazos-jur-${j.value}`}
                onClick={() => handleJurisdiccionChange(j.value)}
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
                  border: `2px solid ${jurisdiccion === j.value ? palette.primary : palette.border}`,
                  borderRadius: 8, background: jurisdiccion === j.value ? palette.primary : "transparent",
                  color: jurisdiccion === j.value ? "white" : palette.text, cursor: "pointer",
                  fontFamily: fonts.body, fontSize: 13, fontWeight: jurisdiccion === j.value ? 600 : 400,
                  transition: "all 0.15s", textAlign: "left",
                }}
              >
                <span style={{ fontSize: 18 }}>{j.icon}</span>
                {j.label}
              </button>
            ))}
          </div>
        </div>

        {/* Parámetros */}
        <div style={cardStyle}>
          <div style={{ marginBottom: 18 }}>
            <label style={lblStyle}>Fecha de la notificación / dies a quo</label>
            <input type="date" data-testid="plazos-fecha" value={fechaNotificacion}
              onChange={(e) => { setFechaNotificacion(e.target.value); setResultado(null); }} style={inputStyle} />
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
            <div style={{ flex: "0 0 100px" }}>
              <label style={lblStyle}>Cantidad</label>
              <input type="number" min={1} max={999} data-testid="plazos-cantidad" value={cantidad}
                onChange={(e) => { setCantidad(e.target.value); setResultado(null); }} style={inputStyle} />
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={lblStyle}>Tipo de plazo</label>
              <select data-testid="plazos-unidad" value={unidad}
                onChange={(e) => { setUnidad(e.target.value); setResultado(null); }}
                style={{ ...inputStyle, appearance: "auto", cursor: "pointer" }}>
                {unidades.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={lblStyle}>Plazos frecuentes</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {presets.map((p, i) => (
                <button
                  key={i}
                  data-testid={`plazos-preset-${i}`}
                  onClick={() => { setCantidad(p.cantidad); setUnidad(p.unidad); setResultado(null); }}
                  style={{
                    padding: "6px 12px", border: `1px solid ${palette.border}`, borderRadius: 20,
                    background: cantidad == p.cantidad && unidad === p.unidad ? palette.accentLight : "transparent",
                    color: palette.text, fontSize: 12, cursor: "pointer", fontFamily: fonts.body,
                    fontWeight: cantidad == p.cantidad && unidad === p.unidad ? 600 : 400,
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={lblStyle}>Comunidad Autónoma</label>
            <select data-testid="plazos-ccaa" value={ccaa}
              onChange={(e) => { setCcaa(e.target.value); setResultado(null); }}
              style={{ ...inputStyle, appearance: "auto", cursor: "pointer" }}>
              {ccaaOptions.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
            </select>
          </div>

          {/* Festivos locales (partido judicial / municipio) */}
          <div style={{ marginBottom: 18 }}>
            <label style={lblStyle}>Municipio (festes locals)</label>
            <select data-testid="plazos-municipio" value={municipio}
              onChange={(e) => { setMunicipio(e.target.value); prefillFestes(e.target.value, fechaNotificacion); setResultado(null); }}
              style={{ ...inputStyle, appearance: "auto", cursor: "pointer" }}>
              <option value="">— Sin festivos locales —</option>
              {Object.entries(FESTES_LOCALS).map(([k, v]) => (
                <option key={k} value={k}>{v.nombre}</option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={{ ...lblStyle, fontSize: 10 }}>Festa local 1</label>
                <input type="date" data-testid="plazos-festa1" value={festaLocal1}
                  onChange={(e) => { setFestaLocal1(e.target.value); setResultado(null); }} style={inputStyle} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={{ ...lblStyle, fontSize: 10 }}>Festa local 2</label>
                <input type="date" data-testid="plazos-festa2" value={festaLocal2}
                  onChange={(e) => { setFestaLocal2(e.target.value); setResultado(null); }} style={inputStyle} />
              </div>
            </div>
            <div style={{ fontSize: 10, color: palette.textMuted, marginTop: 4, fontStyle: "italic" }}>
              Festes locals oficials (DOGC). 2026 precargadas; verifica y añade 2027 según el DOGC.
            </div>
          </div>

          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
            background: augustoInhabil ? palette.accentLight + "55" : palette.inputBg,
            borderRadius: 8, border: `1px solid ${augustoInhabil ? palette.accent : palette.border}`,
          }}>
            <input type="checkbox" data-testid="plazos-agosto" checked={augustoInhabil}
              onChange={(e) => { setAugustoManual(e.target.checked); setResultado(null); }}
              id="agosto" style={{ width: 18, height: 18, accentColor: palette.primary, cursor: "pointer" }} />
            <label htmlFor="agosto" style={{ fontSize: 14, cursor: "pointer", lineHeight: 1.3 }}>
              <strong>Agosto inhábil</strong>
              <span style={{ color: palette.textMuted, fontSize: 12, display: "block" }}>
                Art. 183 LOPJ — Civil y Contencioso-Administrativo
                {augustoManual === null && (<span style={{ fontStyle: "italic" }}> (auto según jurisdicción)</span>)}
              </span>
            </label>
          </div>
        </div>

        <button
          data-testid="plazos-calcular-btn"
          onClick={calcular}
          style={{
            width: "100%", padding: "14px",
            background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryLight} 100%)`,
            color: "white", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 700,
            fontFamily: fonts.body, cursor: "pointer", letterSpacing: "0.02em", marginBottom: 20,
            boxShadow: `0 2px 8px ${palette.primary}44`,
          }}
        >
          Calcular plazo
        </button>

        {resultado && (
          <div style={{ background: palette.card, borderRadius: 10, border: `2px solid ${palette.success}`, overflow: "hidden", marginBottom: 14 }} data-testid="plazos-resultado">
            <div style={{ background: palette.successBg, padding: "16px 20px", borderBottom: `1px solid ${palette.success}33` }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: palette.success, marginBottom: 4 }}>
                Fecha de vencimiento
              </div>
              <div style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 700, color: palette.success }} data-testid="plazos-vencimiento">
                📌 {formatDate(resultado.fechaVencimiento)}
              </div>
              <div style={{ fontFamily: fonts.mono, fontSize: 14, color: palette.textMuted, marginTop: 2 }}>
                {formatDateShort(resultado.fechaVencimiento)}
              </div>
            </div>
            <div style={{ padding: "16px 20px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: palette.textMuted, marginBottom: 8 }}>
                Detalle del cómputo
              </div>
              {resultado.log.map((l, i) => (
                <div key={i} style={{ fontSize: 13, lineHeight: 1.5, padding: "4px 0", borderBottom: i < resultado.log.length - 1 ? `1px solid ${palette.border}44` : "none", color: palette.text }}>
                  <span style={{ color: palette.accent, marginRight: 6, fontWeight: 700 }}>›</span>
                  {l}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PANEL: Guardar plazo en un expediente + agenda */}
        {resultado && !resultado.error && (
          <div style={{ ...cardStyle, borderLeft: `4px solid ${palette.primary}` }} data-testid="plazos-guardar-panel">
            <button
              data-testid="plazos-abrir-guardar"
              onClick={() => { setShowGuardar(!showGuardar); setGuardarRes(null); setGuardarErr(""); }}
              style={{ background: "none", border: "none", cursor: "pointer", fontFamily: fonts.body, fontSize: 14, fontWeight: 700, color: palette.primary, padding: 0, display: "flex", alignItems: "center", gap: 8 }}
            >
              <span style={{ fontSize: 18 }}>📌</span>
              {showGuardar ? "▾" : "▸"} Guardar el plazo en un expediente (crea actuación y aviso en agenda)
            </button>

            {showGuardar && (
              <div style={{ marginTop: 14 }}>
                <div style={{ position: "relative" }}>
                  <label style={lblStyle}>Expediente</label>
                  <input
                    data-testid="plazos-exp-search"
                    placeholder="Buscar por número de expediente o cliente…"
                    value={expSel ? `${expSel.numero_expediente} — ${expSel.cliente_nombre || ""}` : expQuery}
                    onChange={(e) => buscarExp(e.target.value)}
                    style={inputStyle}
                  />
                  {expOpciones.length > 0 && !expSel && (
                    <div style={{ position: "absolute", zIndex: 20, background: "white", border: `1px solid ${palette.border}`, borderRadius: 6, width: "100%", marginTop: 2, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}>
                      {expOpciones.map((ex) => (
                        <button key={ex.id} data-testid={`plazos-exp-opt-${ex.id}`}
                          onClick={() => { setExpSel(ex); setExpOpciones([]); }}
                          style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 13, background: "none", border: "none", cursor: "pointer", borderBottom: `1px solid ${palette.border}33` }}>
                          <b>{ex.numero_expediente}</b> — {ex.cliente_nombre || "Sin cliente"}
                        </button>
                      ))}
                    </div>
                  )}
                  {expSel && (
                    <p style={{ fontSize: 11, color: palette.success, marginTop: 4 }}>
                      {expSel.numero_expediente} ✓
                      <button onClick={() => { setExpSel(null); setExpQuery(""); }} style={{ marginLeft: 8, textDecoration: "underline", background: "none", border: "none", cursor: "pointer", color: palette.textMuted, fontSize: 11 }}>quitar</button>
                    </p>
                  )}
                </div>

                <div style={{ marginTop: 14 }}>
                  <label style={lblStyle}>Describe el tipo de plazo</label>
                  <input
                    data-testid="plazos-tipo-texto"
                    placeholder="Ej: Plazo para interponer recurso de apelación · Contestación a la demanda · Presentación de escrito…"
                    value={tipoPlazoTexto}
                    onChange={(e) => setTipoPlazoTexto(e.target.value)}
                    style={inputStyle}
                  />
                  <div style={{ fontSize: 10, color: palette.textMuted, marginTop: 3 }}>
                    Se usará como título de la actuación. Vencimiento: <b>{formatDateShort(resultado.fechaVencimiento)}</b>.
                  </div>
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" data-testid="plazos-crear-evento" checked={crearEvento} onChange={(e) => setCrearEvento(e.target.checked)} style={{ width: 16, height: 16, accentColor: palette.primary }} />
                  Crear aviso en la agenda (recordatorio 1 día y 1 hora antes del vencimiento)
                </label>

                <button
                  data-testid="plazos-guardar-btn"
                  onClick={guardarEnExpediente}
                  disabled={guardando}
                  style={{ marginTop: 14, width: "100%", padding: "12px", background: guardando ? palette.textMuted : palette.primary, color: "white", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 700, fontFamily: fonts.body, cursor: guardando ? "wait" : "pointer" }}
                >
                  {guardando ? "Guardando…" : "Guardar en el expediente"}
                </button>

                {guardarErr && (
                  <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, background: palette.dangerBg, border: `1px solid ${palette.danger}44`, fontSize: 12, color: palette.danger }} data-testid="plazos-guardar-error">
                    ⚠️ {guardarErr}
                  </div>
                )}
                {guardarRes && guardarRes.ok && (
                  <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 8, background: palette.successBg, border: `1px solid ${palette.success}44`, fontSize: 13, color: palette.text }} data-testid="plazos-guardar-ok">
                    <b style={{ color: palette.success }}>✓ Actuación creada</b> en {expSel?.numero_expediente}
                    {guardarRes.eventoCreado ? " · aviso añadido a la agenda ✓" : ""}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ ...cardStyle, padding: "16px 20px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: palette.textMuted, marginBottom: 10 }}>
            Reglas de cómputo aplicadas
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.7, color: palette.textMuted }}>
            <p style={{ margin: "0 0 6px" }}>
              <strong style={{ color: palette.text }}>Días hábiles (art. 130.2 LEC):</strong>{" "}
              Se excluyen sábados, domingos, festivos nacionales y autonómicos.
              {augustoInhabil && " Agosto completo es inhábil (art. 183 LOPJ)."}
            </p>
            <p style={{ margin: "0 0 6px" }}>
              <strong style={{ color: palette.text }}>Días naturales:</strong>{" "}
              Se cuentan todos los días del calendario. Si el último día es inhábil, se traslada al siguiente hábil.
            </p>
            <p style={{ margin: "0 0 6px" }}>
              <strong style={{ color: palette.text }}>Meses y años (art. 133.3 LEC):</strong>{" "}
              Se computan de fecha a fecha. Si el último día es inhábil, se traslada al siguiente hábil.
            </p>
            <p style={{ margin: 0 }}>
              <strong style={{ color: palette.text }}>Dies a quo (art. 133.1 LEC):</strong>{" "}
              No se incluye el día de la notificación en el cómputo. El plazo empieza a correr al día siguiente.
            </p>
          </div>
        </div>

        <div style={{ textAlign: "center", fontSize: 11, color: palette.textMuted, padding: "8px 0" }}>
          Lex Duo Advocats SLP · Herramienta interna · No sustituye el criterio profesional
        </div>
      </div>
    </div>
  );
}
